import AppAlert from '@nxsys/components/AppAlert';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppDescription from '@nxsys/components/AppDescription';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppRateForm, {
  AppRateFormItem,
  AppRateFormValues,
} from '@nxsys/components/AppRateForm';
import AppRateTable from '@nxsys/components/AppRateTable';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import {
  PaymentFrequencyEnum,
  TimesheetStatusEnum,
} from '@nxsys/constants/AppEnums';
import {
  useGetTimesheetById,
  useUpdateTimesheetById,
} from '@nxsys/hooks/api/timesheets';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import {
  irregularHolidayWorkedHours,
  useIrregularHolidayTimesheetLimit,
} from '@nxsys/hooks/useIrregularHolidayTimesheetLimit';
import { IRELAND } from '@nxsys/store/constants/keys';
import { useMobile } from '@nxsys/hooks/useMobile';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Rate } from '@nxsys/types/models/timesheet';
import { formatStatus } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { getTaxWeekTaxMonthOptionByFrequency } from '@nxsys/utils/taxYear';
import { Descriptions, Form } from 'antd';
import { isEmpty, toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppState } from '@nxsys/store';

const TimesheetDetail = () => {
  const { messages } = useTypedIntl();
  const { id } = useParams();
  const { isMobile } = useMobile();
  const { goBack } = useGoBackByQuery();
  const { isAgency, isEmployee, isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const handleGoBack = () => {
    goBack({
      fromPathQueryKey: 'from',
      fallbackPathDefault: isEmployee
        ? '/timesheets-employee'
        : isAgency
          ? '/timesheets-agency'
          : '/timesheets',
    });
  };

  const [rateForm] = Form.useForm<AppRateFormValues>();

  const {
    run: getTimesheetById,
    value: timesheetValue,
    loading: isGetTimesheetByIdPending,
  } = useGetTimesheetById();

  const {
    run: updateTimesheetById,
    value: updateTimesheetValue,
    loading: isUpdateTimesheetByIdPending,
  } = useUpdateTimesheetById();

  const timesheetData = useMemo(
    () => (updateTimesheetValue ?? timesheetValue)?.data,
    [timesheetValue, updateTimesheetValue],
  );
  const isGeneratedPayroll = useMemo(
    () =>
      !!timesheetData?.newPayroll ||
      (!!timesheetData?.newDraftPayroll &&
        isEmpty(timesheetData?.newDraftPayroll?.errors)),
    [timesheetData],
  );
  const disabledTimesheet = useMemo(
    () =>
      timesheetData?.approveStatus === TimesheetStatusEnum.CANCEL ||
      isGeneratedPayroll ||
      timesheetData?.isEditable === false ||
      isEmployee,
    [timesheetData, isGeneratedPayroll],
  );

  useEffect(() => {
    if (id) {
      getTimesheetById({ id });
    }
  }, [id]);

  // NX-6284: Ireland 8% of worked hours (irregular) — the PRHP hours this
  // timesheet may take, read for its own period with its saved lines left
  // out, i.e. the limit its save is checked against.
  const watchRates = Form.useWatch<AppRateFormItem[]>('rates', rateForm);
  const { subcontractorForRateForm } = useIrregularHolidayTimesheetLimit({
    subcontractor: timesheetData?.subcontractorRef,
    enabled: payrollCountry === IRELAND && !!timesheetData?.id,
    params: {
      timesheetId: timesheetData?.id,
      workedHours: timesheetData?.isIssueP45
        ? irregularHolidayWorkedHours(watchRates)
        : undefined,
    },
    debounce: !!timesheetData?.isIssueP45,
  });

  const renderTitle = useCallback(() => {
    const renderStatus = () => {
      return (
        <AppTagStatus
          className={`tag-popup ${formatStatus(timesheetData?.status ?? '')}`}
        >
          {timesheetData?.status}
        </AppTagStatus>
      );
    };

    return (
      <>
        <div className="flex items-center gap-x-4">
          <AppBackButton onClick={handleGoBack} />
          <div className="flex items-center gap-x-3 max-sm:block">
            <AppPageTitle separator="" prefix={id} title="" />
            {!isMobile && renderStatus()}
          </div>
        </div>
        {isMobile && renderStatus()}
      </>
    );
  }, [messages, id, timesheetData, isMobile]);

  const renderRightContent = useCallback(() => {
    return disabledTimesheet ? null : (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.cancel']}
          onClick={handleGoBack}
          className="max-sm:hidden"
        />
        <AppButton
          type="primary"
          name={messages['common.save']}
          onClick={() => rateForm.submit()}
        />
      </div>
    );
  }, [messages, rateForm, disabledTimesheet]);

  const handleSubmit = async (values: AppRateFormValues) => {
    if (!id) return;
    const payload = {
      rates: values.rates?.map(
        (rate) =>
          ({
            ...timesheetData?.rates?.find((r) => r.rateId === rate.rateId),
            rateId: rate.rateId ?? '',
            rateCode: rate.rateCode?.value ?? '',
            rateType: rate.rateType ?? '',
            description: rate.description ?? '',
            rate: rate.rate ?? '',
            units: toNumber(rate.units),
            vatRate: rate.vatRate ?? '',
            vatDetails: rate.vatDetails?.value ?? '',
            vatCode: rate.vatCode ?? '',
            net: toNumber(rate.net),
            total: toNumber(rate.total),
          }) as Rate,
      ),
    };
    await updateTimesheetById({
      id,
      payload,
    }).then(() => {
      showMessage({
        type: 'success',
        content: messages['timesheet.updateSuccess'],
      });
    });
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isGetTimesheetByIdPending || isUpdateTimesheetByIdPending}
    >
      {isGeneratedPayroll && (
        <AppAlert
          className="mb-4"
          type="warning"
          variant="warning"
          message={messages['timesheet.onlyAllowViewTimesheet']}
        />
      )}
      <AppCard className="mb-4" title={messages['timesheet.title']}>
        <AppDescription
          column={1}
          className="p-4"
          colon={false}
          labelStyle={{ width: '200px' }}
        >
          <Descriptions.Item label={messages['common.subcontractorName']}>
            {timesheetData?.subcontractorRef?.fullName ??
              `${timesheetData?.subcontractorRef?.forename ?? ''} ${timesheetData?.subcontractorRef?.surname ?? ''}`}
          </Descriptions.Item>
          {!isBureau && (
            <>
              <Descriptions.Item label={messages['common.assignment']}>
                {timesheetData?.assignmentRef?.assignmentName ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label={messages['common.agencyName']}>
                {timesheetData?.agencyRef?.name ?? '-'}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label={messages['timesheet.timesheetPeriod']}>
            {timesheetData?.startDate
              ? moment(timesheetData?.startDate).format('DD MMM')
              : ''}
            {' - '}
            {timesheetData?.endDate
              ? moment(timesheetData?.endDate).format('DD MMM')
              : ''}
          </Descriptions.Item>
          <Descriptions.Item label={messages['common.siteId']}>
            {timesheetData?.siteId ?? '-'}
          </Descriptions.Item>
          {/* NX-5237: the tax period is READ-ONLY here. It is changed from the
              timesheet list (row icon / bulk action) — the only surfaces the
              ticket's acceptance criteria name. An inline editor here was never
              an AC; it came from a "Technical notes (for devs)" line on the
              ticket and gave the same change a second internal path with its own
              guards. */}
          <Descriptions.Item label={messages['common.taxPeriod']}>
            {
              getTaxWeekTaxMonthOptionByFrequency(
                timesheetData?.subcontractorRef?.payFrequency ??
                  PaymentFrequencyEnum.WEEKLY,
                timesheetData?.subcontractorRef?.payFrequency ===
                  PaymentFrequencyEnum.MONTHLY
                  ? (timesheetData?.monthConfig ?? 0)
                  : (timesheetData?.taxPeriodConfig ?? 0),
              )?.label
            }
          </Descriptions.Item>
          {/* NX-5237 (#8): surface the tax-period change reason (read-only) to
              anyone viewing the timesheet — the agency AND the internal
              admin/employer who needs to know why the period changed —
              whenever one was captured. */}
          {timesheetData?.updateReason ? (
            <Descriptions.Item label={messages['timesheet.updateReason']}>
              {timesheetData.updateReason}
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label={messages['common.attachment']}>
            {timesheetData?.file ? (
              <AppUploadFile
                urlFile={timesheetData?.file ?? ''}
                onChange={(_: File) => {
                  console.info('file', _);
                }}
                hideDelete
                onClickFile={() =>
                  window.open(timesheetData?.file ?? '', '_blank')
                }
              />
            ) : (
              '-'
            )}
          </Descriptions.Item>
        </AppDescription>
      </AppCard>
      {!isGetTimesheetByIdPending ? (
        !disabledTimesheet ? (
          <AppRateForm
            form={rateForm}
            onFinish={handleSubmit}
            initialValues={{
              rates: (timesheetData?.rates ?? [])?.map((rate) => ({
                ...rate,
                rateCode: {
                  label: rate.rateCode ?? '',
                  value: rate.rateCode ?? '',
                },
                vatDetails: {
                  label: rate.vatDetails ?? '',
                  value: rate.vatDetails ?? '',
                },
                vatCode:
                  (rate as any).vatCode ??
                  // vatDetails format: "<desc> (<VATCode>) - <VATRate>" —
                  // grab the parenthesised group right before " - ".
                  rate.vatDetails?.match(/\(([^()]+)\)\s*-/)?.[1] ??
                  '',
                net: rate.net?.toString() ?? '',
                total: rate.total?.toString() ?? '',
                units: rate.units?.toString() ?? '',
              })),
            }}
            listDisabled={['net', 'total', 'rateType', 'description']}
            disabledDelete={disabledTimesheet}
            disabledAdd={disabledTimesheet}
            subcontractor={subcontractorForRateForm}
          />
        ) : (
          <AppRateTable data={timesheetData?.rates ?? []} />
        )
      ) : null}
    </AppsContainer>
  );
};

export default TimesheetDetail;
